/**
 * Single source of truth for subscription tiers and addon credits.
 * Used by: landing (pricing section), app (firebase.ts re-export), functions (after sync).
 * When changing plan details, edit here and run: npm run sync:plans (then deploy functions if needed).
 */

export const SUBSCRIPTION_TIERS = {
  free: {
    name: 'Free',
    nameHe: 'חינמי',
    monthlyCredits: 50,
    color: 'gray',
    priceMonthlyILS: 0,
    priceAnnualILS: 0
  },
  creator: {
    name: 'Creator',
    nameHe: 'יוצר',
    monthlyCredits: 200,
    color: 'cyan',
    priceMonthlyILS: 39,
    priceAnnualILS: 390
  },
  viral: {
    name: 'Viral',
    nameHe: 'ויראלי',
    monthlyCredits: 600,
    color: 'violet',
    priceMonthlyILS: 99,
    priceAnnualILS: 990
  },
  studio: {
    name: 'Studio',
    nameHe: 'סטודיו',
    monthlyCredits: 2000,
    color: 'yellow',
    priceMonthlyILS: 299,
    priceAnnualILS: 2990
  }
} as const;

export type SubscriptionTier = keyof typeof SUBSCRIPTION_TIERS;

export const ADDON_CREDITS_BY_TIER: Record<
  Exclude<SubscriptionTier, 'free'>,
  { amount: number; priceILS: number }
> = {
  creator: { amount: 100, priceILS: 19.9 },
  viral: { amount: 100, priceILS: 16.9 },
  studio: { amount: 100, priceILS: 14.9 }
};

/** Paid tiers in upgrade order (for iteration / PayPlus). */
export const PAID_TIERS: readonly Exclude<SubscriptionTier, 'free'>[] = ['creator', 'viral', 'studio'];

export const TIER_RANK: Record<SubscriptionTier, number> = {
  free: 0,
  creator: 1,
  viral: 2,
  studio: 3
};

export function normalizeSubscriptionTier(rawTier: unknown): SubscriptionTier {
  if (rawTier === 'pro') return 'creator';
  if (rawTier === 'enterprise') return 'studio';
  if (rawTier && typeof rawTier === 'string' && rawTier in SUBSCRIPTION_TIERS) {
    return rawTier as SubscriptionTier;
  }
  return 'free';
}
